import _ from 'lodash';
import React from 'react';
/* eslint-disable restrict-imports/restrict-imports */
import { makeStyles } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Slider from '@material-ui/core/Slider';
import { withStyles } from '@material-ui/core/styles';
import TranscriptionAudioAttachmentTooltip from './TranscriptionAudioAttachmentTooltip';

const SPEED_STEP = 0.25;
const SPEED_MIN = 0.5;
const SPEED_MAX = 1.5;

const VOLUME_STEP = 0.025;
const VOLUME_MIN = 0.01;
const VOLUME_MAX = 1;

export const ControlConstants: Record<string, number> = {
  speedStep: SPEED_STEP,
  speedMin: SPEED_MIN,
  speedMax: SPEED_MAX,
  volumeStep: VOLUME_STEP,
  volumeMin: VOLUME_MIN,
  volumeMax: VOLUME_MAX,
};

const VolumeSlider = withStyles({
  root: {
    color: '#5e5e5e',
    width: 162,
    marginLeft: 8,
    marginRight: 8,
  },
  thumb: {
    backgroundColor: '#5e5e5e',
    transition: 'box-shadow 250ms ease-out',
    '&:hover': {
      boxShadow: '0 0px 12px rgba(255,255,255,0.7)',
    },
  },
  active: {
    boxShadow: '0 0px 12px rgba(255,255,255,0)',
  },
  focusVisible: {
    boxShadow: '0 0px 12px rgba(255,255,255,0)',
  },
})(Slider);

interface TranscriptionAudioAttachmentControlsProps {
  handleSpeed: (num: number) => void;
  handleVolumeChange: (value: number | number[]) => void;
  handleBack: () => void;
  handleForward: () => void;
  handlePlayPause: () => void;
  loading: boolean;
  playing: boolean;
  currentTime: string;
  duration: string;
  speed: number;
  volume: number;
  timeFontSize: number;
}

export default function TranscriptionAudioAttachmentControls({
  handleSpeed,
  handleVolumeChange,
  handleBack,
  handleForward,
  handlePlayPause,
  loading,
  playing,
  currentTime,
  duration,
  speed,
  volume,
  timeFontSize,
}: TranscriptionAudioAttachmentControlsProps) {
  const classes = useStyles();

  return (
    <div className={classes.controls}>
      <div className={classes.sliders}>
        <div className={classes.slider}>
          <div className={classes.iconLabel}>
            <FontAwesomeIcon icon="rabbit" color="black" />
          </div>
          <div className={classes.tagsWrapper}>
            {_.range(SPEED_MIN, SPEED_MAX + SPEED_STEP, SPEED_STEP).map((num, i) => (
              <div
                key={i}
                className={classes.speedTag}
                style={{
                  color: num === speed ? '#29CD9C' : '#5e5e5e',
                }}
                onClick={() => handleSpeed(num)}
              >
                {num !== 1 ? num : num + 'x'}
              </div>
            ))}
          </div>
        </div>
        <div className={classes.slider}>
          <div className={classes.iconLabel}>
            <FontAwesomeIcon icon="volume" color="black" />
          </div>
          <VolumeSlider
            value={volume}
            onChange={(e: React.ChangeEvent<{}>, value: number | number[]) => {
              handleVolumeChange(value);
            }}
            step={VOLUME_STEP}
            min={VOLUME_MIN}
            max={VOLUME_MAX}
          />
        </div>
      </div>
      <div className={classes.mainControls}>
        <button className={classes.skipButton} onClick={handleBack}>
          <FontAwesomeIcon icon="backward" color="#5e5e5e" />
        </button>
        <button className={classes.playButton} onClick={handlePlayPause} disabled={loading}>
          {playing ? (
            <FontAwesomeIcon icon="pause" color="#5e5e5e" />
          ) : (
            <FontAwesomeIcon icon="play" color="#5e5e5e" />
          )}
        </button>
        <button className={classes.skipButton} onClick={handleForward}>
          <FontAwesomeIcon icon="forward" color="#5e5e5e" />
        </button>
        <div
          className={classes.time}
          style={{ fontSize: timeFontSize }}
        >{`${currentTime} / ${duration}`}</div>
        <TranscriptionAudioAttachmentTooltip />
      </div>
    </div>
  );
}

const useStyles = makeStyles({
  controls: { 'flex-shrink': 0, display: 'flex', justifyContent: 'center' },
  mainControls: {
    padding: 12,
    'flex-shrink': 0,
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',
    justifyContent: 'center',
  },
  sliders: {
    width: 216,
    'flex-shrink': 0,
    padding: '8px 16px',
    'padding-left': 0,
    'border-rigt': 'solid 1px #333',
    color: 'black',
  },
  playButton: {
    outline: 'none',
    border: 'none',
    width: 56,
    height: 56,
    background: '#29CD9C',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 200ms ease-out',
    'box-shadow': '0 1px 4px rgba(41, 205, 156, 0)',
    '&:hover': {
      'box-shadow': ' 0 2px 25px rgba(41, 205, 156, 0.9)',
    },
    '&:disabled': {
      opacity: 0.5,
    },
  },
  skipButton: {
    outline: 'none',
    border: 'none',
    height: 32,
    width: 32,
    padding: 8,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0.6,
    transition: 'background 150ms ease-out',
    cursor: 'pointer',
    background: 'rgba(255,255,255,0)',
    '&:hover': {
      background: 'rgba(255,255,255,0.12)',
    },
  },
  slider: {
    display: 'flex',
    height: 32,
    alignItems: 'center',
    '& > label': {
      width: 48,
      'flex-shrink': 0,
      marginRight: 16,
      fontSize: 12,
      lineHeight: 20,
    },
  },
  iconLabel: {
    height: 16,
    width: 16,
    opacity: 0.5,
    marginRight: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagsWrapper: {
    width: 170,
    'flex-shrink': 0,
    display: 'flex',
    justifyContent: 'space-between',
  },
  speedTag: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 16,
    padding: 2,
    width: 32,
    'flex-shrink': 0,
    borderRadius: 2,
    textAlign: 'center',
    cursor: 'pointer',
    opacity: 0.6,
    '&:hover': {
      opacity: 1,
    },
  },
  time: {
    width: 200,
    'flex-shrink': 0,
    textAlign: 'center',
    color: '#5e5e5e',
    fontSize: 26,
  },
  loading: {
    position: 'absolute',
    left: 96,
    color: 'black',
    opacity: 0.6,
  },
});
